// Portal, Market, API Detail pages
import React, { useState as uS, useMemo as uM, useRef as uR, useEffect as uE } from 'react';
import Balancer from 'react-wrap-balancer';
import { XPrim, XIcons } from '../primitives.jsx';
import { XData } from '../data.jsx';
import { XApi } from '../services.js';
import { XShell } from '../shell.jsx';
import { Select } from '../ui/index.js';
import { useT } from '../i18n.jsx';
import { endpointListCost } from '../endpoint-pricing.js';
// LatencyPopover 已弃用 — 市场卡片改为单一 headlineLatency 数字展示
// (AI = TTFT, 非 AI = P50)，后端 derive。组件文件保留，供未来 detail 页可能复用。

// Single-shot scroll reveal — adds `.is-revealed` to the element once 12% enters the
// viewport, then disconnects. Pair with the `.reveal` class in styles.css. Honours
// prefers-reduced-motion at the CSS layer (no work needed here).
const useReveal = (deps = []) => {
  const ref = uR(null);
  uE(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') { node?.classList?.add('is-revealed'); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('is-revealed'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12 });
    io.observe(node);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
};

// Canonical xapi skill manifest URL — handed to any agent (Claude, Codex, etc.) so it can
// auto-load the xapi tool catalog. Mirrors xapi-new (PortalPage.tsx:22 / MCPPage.tsx:215).
export const XAPI_SKILL_URL = 'https://xapi.to/skill.md';
// Public source for the xAPI skill — surfaced in the Get Started note so users can
// audit / contribute. Same URL used on the homepage and the dedicated skill page.
export const XAPI_SKILL_REPO_URL = 'https://github.com/xapi-labs/xapi-cli/tree/main/skills/xapi';
export const XAPI_PROVIDER_REPO_URL = 'https://github.com/xapi-labs/xapi-provider';

// Renders an i18n string containing a single `{LINK}` placeholder by splitting
// it and replacing the placeholder with an anchor pointing at the xAPI skill
// repo. The visible link label is collapsed to `xapi-labs/xapi-cli ↗` rather
// than the full URL, which on narrow widths used to wrap onto two lines and
// dominate the surrounding copy. Title attr exposes the full URL on hover so
// the user can still verify destination before clicking.
export const renderSkillRepoLine = (text, repo) => {
  const url = repo?.url || XAPI_SKILL_REPO_URL;
  const label = repo?.label || 'xapi-labs/xapi-cli';
  const parts = String(text || '').split('{LINK}');
  return (
    <>
      {parts[0]}
      <a
        href={url}
        target="_blank"
        rel="noreferrer noopener"
        title={url}
        style={{
          color: 'var(--c-mint)',
          textDecoration: 'underline',
          textUnderlineOffset: '2px',
          whiteSpace: 'nowrap',
        }}
      >
        {label} <XIcons.IconExternal size={10} style={{ verticalAlign: '-1px', marginLeft: 1 }}/>
      </a>
      {parts[1] || ''}
    </>
  );
};

// HeroTerminal — three explicit steps inside the Claude CLI aesthetic, each
// preceded by a `── 0X // title ──` divider so the install/auth/use sequence
// reads as a numbered narrative rather than a single terminal flow. Keeps the
// CLI voice (real-looking commands and outputs) consistent with the rest of
// the homepage rhythm.
const StepHeader = ({ n, title, first }) => (
  <div className="row gap-2" style={{ alignItems:'center', marginTop: first ? 0 : 22, color:'var(--fg-4)' }}>
    <span style={{ flex:'0 0 auto' }}>──</span>
    <span style={{ color:'var(--c-mint)', fontFamily:'var(--font-mono)' }}>{n}</span>
    <span style={{ color:'var(--fg-4)' }}>//</span>
    <span style={{ color:'var(--fg-1)', fontWeight: 500 }}>{title}</span>
    <span style={{ flex:'1 1 auto', borderTop:'1px dashed var(--line-2)', marginLeft: 6 }} />
  </div>
);

// 首页四大块：整块可点击（role=button + 键盘 Enter/Space 可达），内部 CTA 已降级为
// 视觉 span，避免嵌套交互元素。cursor:pointer 由内联样式给出。
const ClickableBlock = ({ className, onGo, children }) => (
  <div
    className={className}
    role="button"
    tabIndex={0}
    style={{ cursor: 'pointer' }}
    onClick={onGo}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onGo(); } }}
  >
    {children}
  </div>
);

// One step body line — a `// prose` comment in mono, light grey. Sits between the
// `── 0X // title ──` divider and the actual command line. Reads as a CLI comment
// so the prose explanation stays inside the terminal voice.
const StepBody = ({ children }) => (
  <div className="mono" style={{ paddingLeft: 4, marginTop: 8, color: 'var(--fg-3)', fontSize: 12.5, lineHeight: 1.65 }}>
    <span style={{ color: 'var(--fg-4)' }}>{'// '}</span>{children}
  </div>
);

const HeroTerminal = ({ loaded }) => {
  const { t } = useT();
  return (
    <div className="term-panel" style={{ width:'100%', maxWidth: 560, marginLeft:'auto', transition:'opacity .8s cubic-bezier(.2,.8,.25,1) .15s, transform .8s cubic-bezier(.2,.8,.25,1) .15s', opacity: loaded ? 1 : 0, transform: loaded ? 'none' : 'translateY(14px)' }}>
      <div className="term-panel-head row between">
        <div className="row gap-2" style={{alignItems:'center'}}>
          <span className="dot"/><span className="dot"/><span className="dot"/>
          <span className="micro" style={{marginLeft: 6, color:'var(--fg-3)'}}>claude</span>
        </div>
        <span className="micro-mono" style={{color:'var(--fg-4)', textTransform:'none', letterSpacing: 0}}>~/agent · v2.5</span>
      </div>
      <div className="term-panel-body">
        {/* Step 1 — install command. Title + prose body + actual shell line. */}
        <StepHeader n="01" title={t('portal.howto.s1.title')} first />
        <StepBody>{t('portal.howto.s1.body')}</StepBody>
        <div style={{paddingLeft: 4, marginTop: 8}}>
          <span className="term-prompt">$</span>
          <span style={{color:'var(--fg-1)'}}>npx skills add </span>
          <span className="term-string">xapi-labs/xapi-cli</span>
        </div>

        {/* Step 2 — hand the agent an API key via chat (not shell). The previous
            `$ export XAPI_KEY=...` was misleading — shell env-vars don't reach
            an LLM agent in another process. The realistic flow is: sign up on
            the console, then paste the key into the agent chat the first time
            it asks. */}
        <StepHeader n="02" title={t('portal.howto.s2.title')} />
        <StepBody>{t('portal.howto.s2.body')}</StepBody>
        <div style={{marginTop: 10, color:'var(--fg-2)', paddingLeft: 4}}>
          <span style={{color:'var(--c-mint)'}}>{'>'}</span>{' '}
          {(() => {
            const parts = t('portal.howto.s2.example').split('{CODE}');
            return <>{parts[0]}<span className="term-string">xapito</span>{parts[1] || ''}</>;
          })()}
        </div>

        {/* Step 3 — see it work. The body's example query also serves as the prompt. */}
        <StepHeader n="03" title={t('portal.howto.s3.title')} />
        <StepBody>{t('portal.howto.s3.body')}</StepBody>
        <div style={{marginTop: 10, color:'var(--fg-2)', paddingLeft: 4}}>
          <span style={{color:'var(--c-mint)'}}>{'>'}</span> {t('portal.howto.s3.example')}
        </div>
      </div>
    </div>
  );
};

// fmtStat — compact human number for the homepage stats bar (1.7M+, 35K, 99.9%).
// Returns an em-dash for missing/zero so an empty platform reads as "—" not "0".
const fmtStat = (v, suffix = '') => {
  const n = Number(v);
  if (!isFinite(n) || n <= 0) return '—';
  if (n >= 1e6) return (n / 1e6).toFixed(n >= 1e7 ? 0 : 1).replace(/\.0$/, '') + 'M' + suffix;
  if (n >= 1e3) return (n / 1e3).toFixed(n >= 1e4 ? 0 : 1).replace(/\.0$/, '') + 'K' + suffix;
  return Math.round(n).toLocaleString() + suffix;
};

// HeroTabTerminal — the hero's right-hand terminal, now tabbed between the
// caller flow (install CLI → ask → get answer, reusing portal.howto.*) and the
// provider flow (install provider skill → publish → live, portal.hero.tl* +
// portal.feat.skill.*). Same Claude-CLI aesthetic + StepHeader/StepBody rhythm
// as the old single-track HeroTerminal.
const HeroTabTerminal = ({ loaded }) => {
  const { t } = useT();
  const [prov, setProv] = React.useState(false);
  const steps = prov
    ? [
        { n:'01', title:t('portal.hero.tl1'), body:t('portal.hero.tlb1'),
          shell:<><span className="term-prompt">$</span><span style={{color:'var(--fg-1)'}}>npx skills add </span><span className="term-string">xapi-labs/xapi-provider</span></> },
        { n:'02', title:t('portal.hero.tl2'), body:t('portal.hero.tlb2'),
          shell:<><span style={{color:'var(--c-mint)'}}>{'>'}</span> <span style={{color:'var(--fg-2)'}}>{t('portal.feat.skill.line')}</span></> },
        { n:'03', title:t('portal.hero.tl3'), body:t('portal.hero.tlb3'),
          shell:<><span style={{color:'var(--c-mint)'}}>✓</span> <span style={{color:'var(--c-mint)'}}>{t('portal.feat.skill.ok')}</span></> },
      ]
    : [
        { n:'01', title:t('portal.howto.s1.title'), body:t('portal.howto.s1.body'),
          shell:<><span className="term-prompt">$</span><span style={{color:'var(--fg-1)'}}>npx skills add </span><span className="term-string">xapi-labs/xapi-cli</span></> },
        { n:'02', title:t('portal.howto.s2.title'), body:t('portal.howto.s2.body'),
          shell:(() => { const parts = t('portal.howto.s2.example').split('{CODE}');
            return <><span style={{color:'var(--c-mint)'}}>{'>'}</span> <span style={{color:'var(--fg-2)'}}>{parts[0]}<span className="term-string">xapito</span>{parts[1] || ''}</span></>; })() },
        { n:'03', title:t('portal.howto.s3.title'), body:t('portal.howto.s3.body'),
          shell:<><span style={{color:'var(--c-mint)'}}>{'>'}</span> <span style={{color:'var(--fg-2)'}}>{t('portal.howto.s3.example')}</span></> },
      ];
  return (
    <div className="term-panel hero-tabterm" style={{ width:'100%', maxWidth: 560, marginLeft:'auto', transition:'opacity .8s cubic-bezier(.2,.8,.25,1) .15s, transform .8s cubic-bezier(.2,.8,.25,1) .15s', opacity: loaded ? 1 : 0, transform: loaded ? 'none' : 'translateY(14px)' }}>
      <div className="term-panel-head row between">
        <div className="row gap-2" style={{alignItems:'center'}}>
          <span className="dot"/><span className="dot"/><span className="dot"/>
          <span className="micro" style={{marginLeft: 6, color:'var(--fg-3)'}}>claude</span>
        </div>
        <div className="term-tabs" role="tablist">
          <button role="tab" aria-selected={!prov} className={!prov ? 'on' : ''} onClick={() => setProv(false)}>{t('portal.feat.b1.chip')}</button>
          <button role="tab" aria-selected={prov} className={prov ? 'on' : ''} onClick={() => setProv(true)}>{t('portal.feat.b2.chip')}</button>
        </div>
      </div>
      {/* key={prov} replays the quick fade when the caller/provider tab swaps
          the step list — terminal content teleporting felt like a glitch. */}
      <div key={prov ? 'prov' : 'caller'} className="term-panel-body hero-tabterm-body apx-fade">
        {steps.map((s, i) => (
          <React.Fragment key={s.n}>
            <StepHeader n={s.n} title={s.title} first={i === 0} />
            <StepBody>{s.body}</StepBody>
            <div style={{marginTop: i === 0 ? 8 : 10, paddingLeft: 4}}>{s.shell}</div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// Splits a slash command on its leading `/xapi` token so the prefix can be
// rendered with the brand-mint accent — same trick we use for the home hero
// "//" punctuation. Falls back to a single span when the prefix isn't there.
const renderSlashCmd = (cmd) => {
  const m = /^(\/xapi\b)(.*)$/.exec(cmd || '');
  if (!m) return <span style={{color:'var(--fg-1)'}}>{cmd}</span>;
  return <><span className="gs-cmd-prefix">{m[1]}</span><span style={{color:'var(--fg-1)'}}>{m[2]}</span></>;
};

// CommandLine — terminal-styled `$ <cmd>` row with a copy button + a blinking
// caret at the end. Used by the Get Started section's install blocks.
const CommandLine = ({ cmd }) => {
  const [copied, setCopied] = React.useState(false);
  const onCopy = async () => {
    try { await navigator.clipboard.writeText(cmd); setCopied(true); setTimeout(()=>setCopied(false), 1200); } catch {}
  };
  return (
    <div className="gs-cmdline row between">
      <div className="gs-cmdline-text">
        <span className="term-prompt">$</span>
        <span style={{color:'var(--fg-1)'}}>{cmd}</span>
        <span className="gs-caret" aria-hidden="true">▍</span>
      </div>
      <button onClick={onCopy} className="btn btn-sm btn-ghost btn-icon" title="Copy" style={{flexShrink: 0}}>
        {copied ? <XIcons.IconCheck size={12} style={{color:'var(--ok)'}}/> : <XIcons.IconCopy size={12}/>}
      </button>
    </div>
  );
};

// ExampleRow — slash-command chip. Used in the Get Started section's "Use it"
// panel; staggered fade-up via `idx`. Hint copy was removed — the commands
// speak for themselves and trailing prose just clutters the row.
const ExampleRow = ({ cmd, idx = 0 }) => {
  const ref = useReveal();
  return (
    <div
      ref={ref}
      className={`gs-ex-row reveal delay-${Math.min(idx + 1, 3)}`}
    >
      <div className="gs-ex-cmd mono">
        {renderSlashCmd(cmd)}
      </div>
    </div>
  );
};

// CollapsibleBlock — generic expand/hide row used by the Get Started section
// for "Install the CLI" and "Stay updated". Closed by default; chevron rotates
// on open. Body content is fully render-controlled by `children`.
const CollapsibleBlock = ({ n, title, badge, children }) => {
  const { t } = useT();
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ borderTop:'1px solid var(--line)' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="row between"
        style={{
          width:'100%', padding:'18px 0',
          background:'transparent', border:'none', cursor:'pointer',
          color:'inherit', textAlign:'left', alignItems:'center', gap: 12,
        }}>
        <div className="row gap-3" style={{alignItems:'center', minWidth: 0, flex: 1}}>
          <span className="mono" style={{color:'var(--c-mint)', fontSize: 12, flexShrink: 0}}>{n}</span>
          <span style={{fontSize: 17, fontWeight: 500, color:'var(--fg-1)'}}>{title}</span>
          {badge && (
            <span className="mono" style={{
              fontSize: 10, letterSpacing:'0.08em',
              padding:'3px 7px', borderRadius: 3,
              background:'var(--bg-2)', border:'1px solid var(--line-2)', color:'var(--fg-3)',
              flexShrink: 0,
            }}>{badge}</span>
          )}
        </div>
        <span className="row gap-2" style={{alignItems:'center', color:'var(--fg-3)', fontSize: 12, flexShrink: 0}}>
          <span className="micro-mono" style={{textTransform:'none', letterSpacing: 0}}>
            {open ? t('portal.getstarted.collapse') : t('portal.getstarted.expand')}
          </span>
          <XIcons.IconChevronDown size={13} style={{transform: open ? 'rotate(180deg)' : 'none', transition:'transform var(--dur-fast) var(--ease-out)'}}/>
        </span>
      </button>
      {/* grid-template-rows 0fr→1fr is the modern way to animate "height: auto":
          the child always renders (no mount/unmount pop), the row track does
          the measuring. Occasional interaction, so the layout-property cost is
          fine; 250ms strong ease-out keeps it snappy. */}
      <div style={{
        display: 'grid',
        gridTemplateRows: open ? '1fr' : '0fr',
        transition: 'grid-template-rows var(--dur-panel) var(--ease-out)',
      }}>
        {/* visibility keeps collapsed content out of the tab order / a11y tree:
            immediate on open, deferred until the collapse finishes on close. */}
        <div style={{ overflow: 'hidden', minHeight: 0, visibility: open ? 'visible' : 'hidden', transition: 'visibility 0s', transitionDelay: open ? '0s' : 'var(--dur-panel)' }}>
          <div style={{
            paddingBottom: 24,
            opacity: open ? 1 : 0,
            transition: 'opacity var(--dur-ui) var(--ease-out)',
          }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

// GetStartedSection — homepage onboarding block. Two visible steps (install +
// use) plus two collapsible blocks (CLI install, stay updated). Same dark /
// monospace voice as the rest of the homepage; layout inspired by
// pbakaus/impeccable but visually re-skinned to xapi's terminal aesthetic.
const GetStartedSection = ({ navigate }) => {
  const { t } = useT();
  return (
    <RevealSection className="sec-pad-regular section-seam" style={{ background:'var(--bg)' }}>
      <div className="reveal-stagger" style={{ maxWidth: 1480, margin:'0 auto', padding:'0 40px' }}>
        <div style={{ marginBottom: 44 }}>
          <div className="sec-num"><span className="grey">04</span> &nbsp;{t('portal.getstarted.eyebrow')}</div>
          <h2 className="gs-section-title" style={{ margin:'14px 0 0', fontSize:'clamp(32px, 4.8vw, 56px)', letterSpacing:'-0.035em', fontWeight: 600, lineHeight: 1.02, textWrap:'balance' }}>
            {t('portal.getstarted.titleA')}{' '}
            <span className="gs-section-title-tail">
              <span className="slash-mark">//</span> <span style={{color:'var(--fg-3)'}}>{t('portal.getstarted.titleB')}</span>
            </span>
          </h2>
        </div>

        {/* Two-step grid — Install on the left, Use on the right. Stacks on mobile. */}
        <div className="getstarted-grid" style={{
          display:'grid', gridTemplateColumns:'1fr 1fr', gap: 32,
          marginBottom: 32,
        }}>
          {/* Step 01 — Install */}
          <div>
            <StepHeader n="01" title={t('portal.getstarted.s1.title')} first />
            <p className="gs-body-desktop" style={{margin:'14px 0 18px', color:'var(--fg-2)', fontSize: 14, lineHeight: 1.65, maxWidth: 520}}>
              {t('portal.getstarted.s1.body')}
            </p>
            <p className="gs-body-mobile" style={{margin:'14px 0 18px', color:'var(--fg-2)', fontSize: 14, lineHeight: 1.55, maxWidth: 520}}>
              {t('portal.getstarted.s1.bodyMobile')}
            </p>
            <CommandLine cmd={t('portal.getstarted.s1.cmd')}/>
            <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-4)', marginTop: 10}}>
              {renderSkillRepoLine(t('portal.getstarted.s1.note'))}
            </div>
          </div>

          {/* Step 02 — Use */}
          <div>
            <StepHeader n="02" title={t('portal.getstarted.s2.title')} first />
            <p className="gs-body-desktop" style={{margin:'14px 0 8px', color:'var(--fg-2)', fontSize: 14, lineHeight: 1.65, maxWidth: 520}}>
              {t('portal.getstarted.s2.body')}
            </p>
            <p className="gs-body-mobile" style={{margin:'14px 0 8px', color:'var(--fg-2)', fontSize: 14, lineHeight: 1.55, maxWidth: 520}}>
              {t('portal.getstarted.s2.bodyMobile')}
            </p>
            <div>
              <ExampleRow idx={0} cmd={t('portal.getstarted.s2.ex1.cmd')}/>
              <ExampleRow idx={1} cmd={t('portal.getstarted.s2.ex2.cmd')}/>
              <ExampleRow idx={2} cmd={t('portal.getstarted.s2.ex3.cmd')}/>
            </div>
          </div>
        </div>

        {/* Collapsible blocks — secondary onboarding steps live behind a click so
            the hero of this section stays the two-step path. */}
        <div style={{ borderBottom:'1px solid var(--line)' }}>
          <CollapsibleBlock
            n="03"
            title={t('portal.getstarted.cli.title')}
            badge={t('portal.getstarted.cli.badge')}
          >
            <p style={{margin:'0 0 14px', color:'var(--fg-2)', fontSize: 14, lineHeight: 1.65, maxWidth: 720}}>
              {t('portal.getstarted.cli.body')}
            </p>
            <CommandLine cmd={t('portal.getstarted.cli.cmd')}/>
            <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-4)', marginTop: 10}}>
              {t('portal.getstarted.cli.note')}
            </div>
          </CollapsibleBlock>
          <CollapsibleBlock
            n="04"
            title={t('portal.getstarted.stay.title')}
          >
            <p style={{margin:'0 0 14px', color:'var(--fg-2)', fontSize: 14, lineHeight: 1.65, maxWidth: 720}}>
              {t('portal.getstarted.stay.body')}
            </p>
            <a href="https://x.com/xapi_to" target="_blank" rel="noreferrer noopener" className="btn btn-sm" style={{textDecoration:'none', display:'inline-flex'}}>
              <XIcons.IconTwitter size={12}/> {t('portal.getstarted.stay.cta')} <XIcons.IconExternal size={11}/>
            </a>
          </CollapsibleBlock>
        </div>
      </div>
    </RevealSection>
  );
};

// CapabilityCell — capabilities-grid cell. Editorial card with mono numbered kicker,
// big title, body copy, and a mono fact-line at the bottom. The kicker number
// is colour-keyed by `tint` (mint / grey / amber). 2×2 grid usage.
const CapabilityCell = ({ n, tint = 'mint', kicker, title, body, fact }) => (
  <div className="capability-cell">
    <div className="row gap-3" style={{ alignItems:'baseline' }}>
      <div className={`cell-num ${tint}`}>{n}</div>
      <div className="cell-kicker">{kicker}</div>
    </div>
    <h3 className="cell-title">{title}</h3>
    <p className="cell-body">{body}</p>
    <div className="cell-fact">{fact}</div>
  </div>
);

// BridgeVisual — compact "agent → xapi → API" diagram with curved connector
// paths and traveling-light pulses. Sits inside section 02 in place of the
// old four-card cap grid. The premise: any agent reaches every provider API
// through a single xapi key. Pulses fire on random agent↔hub and hub↔API
// pairs every ~900ms to telegraph live routing.
//
// Brand logos are inline SVG paths from simple-icons (CC0 license) so we
// don't ship a runtime icon dependency. Chips render the real wordmark/glyph
// in the brand's accent colour. Connector lines are quadratic bezier curves
// (more organic than straight strokes), with the pulse ridden along the same
// curve via SVG <animateMotion>. Tight max-width keeps the visual focused
// rather than spread across the full viewport.
const BR_LOGOS = {
  // 24×24 viewBox monochrome paths from simple-icons (CC0) — extracted to
  // single <path d="..."/> form. Brand colour applied via fill at render time.
  claude:  'M4.709 15.955l4.72-2.647.079-.23-.079-.128H9.2l-.79-.048-2.698-.073-2.339-.097-2.266-.122-.571-.121L0 11.755l.055-.352.48-.321.686.06 1.52.103 2.278.158 1.652.097 2.448.255h.389l.055-.158-.134-.097-.103-.097-2.357-1.595-2.552-1.688-1.336-.972-.722-.491-.365-.462-.158-1.008.656-.722.881.06.225.061.893.686 1.908 1.476 2.491 1.833.365.304.146-.103.018-.073-.164-.274-1.355-2.448-1.446-2.49-.644-1.032-.17-.62a2.97 2.97 0 0 1-.104-.729L6.283.134 6.696 0l.996.134.42.365.62 1.417 1.002 2.229 1.555 3.03.456.901.243.834.091.255h.158V8.78l.128-1.711.237-2.099.23-2.703.08-.76.376-.91.747-.493.583.28.48.685-.067.444-.286 1.852-.559 2.903-.365 1.942h.213l.243-.243.985-1.306 1.652-2.063.73-.82.85-.905.547-.434h1.033l.76 1.13-.34 1.166-1.064 1.347-.881 1.142-1.264 1.7-.79 1.36.073.11.188-.02 2.856-.606 1.543-.28 1.841-.315.833.388.092.395-.328.807-1.969.486-2.309.462-3.439.813-.042.03.049.061 1.549.146.662.036h1.622l3.02.225.79.522.474.638-.079.485-1.215.62-1.64-.389-3.829-.91-1.312-.329h-.182v.11l1.093 1.068 2.006 1.81 2.509 2.33.127.578-.322.455-.34-.049-2.205-1.658-.851-.747-1.926-1.622h-.128v.17l.444.649 2.345 3.521.122 1.08-.17.353-.608.213-.668-.122-1.374-1.926-1.415-2.169-1.143-1.943-.14.08-.674 7.254-.316.37-.729.28-.607-.461-.322-.747.322-1.476.389-1.926.315-1.531.286-1.9.17-.632-.012-.042-.14.018-1.434 1.967-2.18 2.945-1.726 1.845-.414.164-.717-.37.066-.662.401-.589 2.388-3.036 1.44-1.882.93-1.087-.006-.158h-.055L4.132 18.56l-1.13.146-.487-.456.061-.746.232-.243 1.913-1.312-.012-.012z',
  cursor:  'M11.925 24l10.425-6-10.425-6L1.5 18l10.425 6zm10.425-6V6L11.925 0v12l10.425 6z',
  openai:  'M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v3l-2.597 1.5-2.607-1.5Z',
  twitter: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
  tiktok:  'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
  reddit:  'M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12.5c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z',
  google:  'M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z',
  sms:     'M12 2C6.477 2 2 6.477 2 12c0 1.79.47 3.47 1.297 4.93L2 22l5.27-1.297A9.95 9.95 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2zM7.5 13a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm4.5 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm4.5 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2z',
};

const BRIDGE_AGENTS = [
  { id: 'claude', label: 'Claude', logo: 'claude', tone: '#d97a4a' },
  { id: 'cursor', label: 'Cursor', logo: 'cursor', tone: '#e5e5e5' },
  { id: 'codex',  label: 'Codex',  logo: 'openai', tone: '#10a37f' },
];
const BRIDGE_APIS = [
  { id: 'twitter', label: 'Twitter', logo: 'twitter', tone: '#e7e9ea' },
  { id: 'tiktok',  label: 'TikTok',  logo: 'tiktok',  tone: '#ff3b5c' },
  { id: '5sim',    label: '5sim',    logo: 'sms',     tone: '#5fd0a8' },
  { id: 'reddit',  label: 'Reddit',  logo: 'reddit',  tone: '#ff5722' },
  { id: 'openai',  label: 'OpenAI',  logo: 'openai',  tone: '#10a37f' },
  { id: 'google',  label: 'Google',  logo: 'google',  tone: '#4285f4' },
];

const BridgeNode = React.forwardRef(({ logo, label, tone, hub, onHoverEnter, onHoverLeave }, ref) => (
  <div
    ref={ref}
    className={hub ? 'bridge-node bridge-hub' : 'bridge-node'}
    onMouseEnter={onHoverEnter}
    onMouseLeave={onHoverLeave}
  >
    {hub ? (
      <span className="bridge-logo bridge-logo-hub">xapi</span>
    ) : (
      <span className="bridge-logo" style={{ color: tone }}>
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path d={BR_LOGOS[logo]} fill="currentColor"/>
        </svg>
      </span>
    )}
    <span className="bridge-label">{label}</span>
  </div>
));

const BridgeVisual = () => {
  const containerRef = uR(null);
  const hubRef = uR(null);
  const agentRefs = uR(BRIDGE_AGENTS.map(() => React.createRef()));
  const apiRefs = uR(BRIDGE_APIS.map(() => React.createRef()));

  // Connection coordinates — measured in container-local px and rebuilt as
  // bezier paths. We stash:
  //   • `left` / `right` — per-line bezier strings (used to paint both the
  //     dim base lines and the bright active pair)
  //   • `hub` — centre point for the ambient ripple rings.
  const [paths, setPaths] = uS({ left: [], right: [], hub: { cx: 0, cy: 0 } });
  uE(() => {
    const compute = () => {
      const c = containerRef.current;
      const hub = hubRef.current;
      if (!c || !hub) return;
      const cb = c.getBoundingClientRect();
      const hb = hub.getBoundingClientRect();
      const hubLeft  = { x: hb.left - cb.left,             y: hb.top + hb.height / 2 - cb.top };
      const hubRight = { x: hb.left + hb.width - cb.left,  y: hb.top + hb.height / 2 - cb.top };
      const hubCentre = { cx: hb.left + hb.width / 2 - cb.left, cy: hb.top + hb.height / 2 - cb.top };

      const curve = (x1, y1, x2, y2) => {
        // Cubic bezier — control points pull horizontally toward the midpoint
        // without vertical offset, producing a soft S that arcs smoothly.
        const dx = x2 - x1;
        const cx1 = x1 + dx * 0.55;
        const cx2 = x2 - dx * 0.55;
        return `M ${x1} ${y1} C ${cx1} ${y1}, ${cx2} ${y2}, ${x2} ${y2}`;
      };

      const agentEnds = agentRefs.current.map((r) => {
        const el = r.current;
        if (!el) return null;
        const b = el.getBoundingClientRect();
        return { x: b.left + b.width - cb.left, y: b.top + b.height / 2 - cb.top };
      });
      const apiStarts = apiRefs.current.map((r) => {
        const el = r.current;
        if (!el) return null;
        const b = el.getBoundingClientRect();
        return { x: b.left - cb.left, y: b.top + b.height / 2 - cb.top };
      });

      const left  = agentEnds.map((p) => p ? curve(p.x, p.y, hubLeft.x,  hubLeft.y) : '');
      const right = apiStarts.map((p) => p ? curve(hubRight.x, hubRight.y, p.x, p.y) : '');

      setPaths({ left, right, hub: hubCentre });
    };
    compute();
    let ro;
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      ro = new ResizeObserver(compute);
      ro.observe(containerRef.current);
    }
    window.addEventListener('resize', compute);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, []);

  // Flow spawner — one continuous "stream" at a time, picked as a connected
  // pair (agent → hub → API). The two highlighted lines therefore always form
  // a single end-to-end path through the hub, never a random disconnected
  // pair. The "flow" effect is intrinsic to the lines themselves — when a
  // pair is active, both lines render with bright stroke + animated
  // stroke-dashoffset (CSS keyframe), so the dashes appear to march from
  // agent through hub to api like water through a pipe. No separate droplet
  // elements means no coordinate-sync bugs and no ghost glow at SVG (0,0).
  const FLOW_MS = 3200;
  const counterRef = uR(0);
  const [flow, setFlow] = uS(() => ({
    id: 0,
    lIdx: Math.floor(Math.random() * BRIDGE_AGENTS.length),
    rIdx: Math.floor(Math.random() * BRIDGE_APIS.length),
  }));

  // Hover-pin — when the user hovers an agent chip, the active *left* line
  // freezes on that agent (right side keeps cycling). Hovering an api chip
  // pins the *right* line. Releasing the hover restores the random cycle on
  // that side. We track pinned indices independently so the two sides never
  // interfere with each other. Declared AFTER `flow` so the index resolution
  // can read flow.lIdx / rIdx without hitting a TDZ.
  const [hoverL, setHoverL] = uS(null); // number | null
  const [hoverR, setHoverR] = uS(null);
  const activeLIdx = hoverL != null ? hoverL : flow.lIdx;
  const activeRIdx = hoverR != null ? hoverR : flow.rIdx;
  uE(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => {
      // Pick a new (agent, api) pair for the next flow. Avoid repeating the
      // same agent two flows in a row so the eye sees variety.
      setFlow((prev) => {
        let lIdx = Math.floor(Math.random() * BRIDGE_AGENTS.length);
        if (BRIDGE_AGENTS.length > 1 && lIdx === prev.lIdx) {
          lIdx = (lIdx + 1) % BRIDGE_AGENTS.length;
        }
        return {
          id: ++counterRef.current,
          lIdx,
          rIdx: Math.floor(Math.random() * BRIDGE_APIS.length),
        };
      });
    }, FLOW_MS);
    return () => clearInterval(id);
  }, [FLOW_MS]);

  return (
    <div ref={containerRef} className="bridge">
      <div className="bridge-col bridge-col-left">
        {BRIDGE_AGENTS.map((a, i) => (
          <BridgeNode
            key={a.id}
            ref={agentRefs.current[i]}
            {...a}
            onHoverEnter={() => setHoverL(i)}
            onHoverLeave={() => setHoverL(null)}
          />
        ))}
        {/* Aggregate indicator — telegraphs that the chip list is a sample,
            not the full set. Static (no animation, no connector line) so it
            doesn't compete with the active flow. */}
        <span className="bridge-more" aria-hidden="true">+ more agents</span>
      </div>
      <div className="bridge-col bridge-col-mid">
        <BridgeNode ref={hubRef} hub label="" />
      </div>
      <div className="bridge-col bridge-col-right">
        {BRIDGE_APIS.map((a, i) => (
          <BridgeNode
            key={a.id}
            ref={apiRefs.current[i]}
            {...a}
            onHoverEnter={() => setHoverR(i)}
            onHoverLeave={() => setHoverR(null)}
          />
        ))}
        <span className="bridge-more" aria-hidden="true">+ 500 more APIs</span>
      </div>
      <svg className="bridge-svg" aria-hidden="true">
        {/* SVG <filter> intentionally NOT used for the active-line glow:
            with the default `filterUnits="objectBoundingBox"`, a near-
            horizontal path (e.g. middle-agent → hub when their y values
            line up) has a 0-height bounding box, so any percentage-based
            filter region collapses to 0 and the path gets clipped to
            nothing. CSS `drop-shadow()` (applied via inline style on the
            <path> below) doesn't have this clipping behavior, so the glow
            renders correctly regardless of the line's geometry. */}

        {/* Concentric ripple rings around the hub — staggered so a new one
            blooms outward every ~1s, conveying constant live activity. */}
        {paths.hub.cx > 0 && [0, 1, 2].map((i) => (
          <circle
            key={'ring' + i}
            cx={paths.hub.cx} cy={paths.hub.cy} r="34"
            fill="none"
            stroke="rgba(46,204,113,0.25)"
            strokeWidth="1"
            className={`bridge-ring bridge-ring-${i}`}
          />
        ))}

        {/* Connection lines — dashed in both states so the paths read as
            conduits, not solid wires. Inactive lines stay dim and static.
            The two lines that match the current flow (left[lIdx] + right[rIdx])
            switch to a brighter stroke with the `bridge-line-flow` class,
            which animates `stroke-dashoffset` so the dashes appear to march
            from agent through hub to api — the line itself becomes the
            "water flowing" visual. No separate droplet elements means no
            position drift between dot and line, and no Cursor-line glitch:
            the active stroke is solid bright across the entire path so the
            line never visually "fades out" near an endpoint. */}
        {paths.left.map((d, i) => {
          const active = i === activeLIdx;
          return (
            <path
              key={'l' + i}
              id={`bridge-path-l-${i}`}
              d={d}
              fill="none"
              stroke={active ? 'rgba(46,204,113,0.85)' : 'rgba(120,180,140,0.28)'}
              strokeWidth={active ? 1.7 : 1}
              strokeDasharray="6 5"
              className={active ? 'bridge-line-flow' : undefined}
              style={{
                transition: 'stroke 0.45s ease, stroke-width 0.45s ease, filter 0.3s ease',
                filter: active ? 'drop-shadow(0 0 4px rgba(46,204,113,0.7))' : 'none',
              }}
            />
          );
        })}
        {paths.right.map((d, i) => {
          const active = i === activeRIdx;
          return (
            <path
              key={'r' + i}
              id={`bridge-path-r-${i}`}
              d={d}
              fill="none"
              stroke={active ? 'rgba(46,204,113,0.85)' : 'rgba(120,180,140,0.28)'}
              strokeWidth={active ? 1.7 : 1}
              strokeDasharray="6 5"
              className={active ? 'bridge-line-flow' : undefined}
              style={{
                transition: 'stroke 0.45s ease, stroke-width 0.45s ease, filter 0.3s ease',
                filter: active ? 'drop-shadow(0 0 4px rgba(46,204,113,0.7))' : 'none',
              }}
            />
          );
        })}

      </svg>
    </div>
  );
};

// ApiMarquee — dual-row infinite scroll of API cards. Each row is duplicated 2x
// inside a flex container animated with translateX(-50%) for a seamless loop.
// Cards are clickable and navigate to api-detail. Hover anywhere in a row pauses
// the animation (so you can actually click).
//
// When the platform has fewer items than fits a row, we repeat the array until
// we have at least MIN_ROW (8) cards per row — otherwise the marquee mid-cycle
// shows large empty gaps (the right-edge "missing cards" bug). After repeating,
// the marquee-row markup duplicates the row a second time for the loop join, so
// total rendered cards per row = unique × 2.
const ApiMarquee = ({ items, navigate }) => {
  const { t } = useT();
  const MIN_ROW = 8;
  const repeat = (arr, min) => {
    if (!arr || arr.length === 0) return [];
    const out = [];
    while (out.length < min) out.push(...arr);
    return out;
  };
  const padded = repeat(items, MIN_ROW * 2);
  const half = Math.ceil(padded.length / 2);
  const row1 = repeat(padded.slice(0, half), MIN_ROW);
  // Stagger row 2 by an offset so the two rows don't visually echo each other.
  const offset = Math.min(2, items.length || 0);
  const row2 = repeat([...padded.slice(half + offset), ...padded.slice(0, half + offset)], MIN_ROW);
  const Card = ({ a, k }) => {
    const meta = XPrim.categoryMeta(a.category);
    const catVars = meta.hue != null
      ? { '--cat-bg': meta.bg, '--cat-border': meta.border, '--cat-dot': meta.dot, '--cat-fg': meta.fg }
      : null;
    return (
    <div key={k} className="mq-card" onClick={() => navigate('api-detail', { id: XApi.helpers.apiSlug(a) })}
      style={{ cursor:'pointer', ...(catVars || {}) }}>
      <div className="mq-row" style={{ marginBottom: 14 }}>
        <ApiAvatar name={a.name} logoUrl={a.logoUrl} meta={meta} size={42} radius={9} fontSize={16}/>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color:'var(--fg-0)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', letterSpacing:'-0.01em' }}>{a.name}</div>
          <div style={{
            marginTop: 4,
            fontFamily:'var(--font-mono)', fontSize: 10.5, fontWeight: 600,
            letterSpacing:'0.08em', textTransform:'uppercase',
            color: meta.hue != null ? meta.fg : 'var(--fg-3)',
          }}>
            {XPrim.categoryLabel(t, a.category) || 'DATA'}
          </div>
        </div>
        <span className="row gap-1" style={{ alignItems:'center', padding:'2px 7px', borderRadius: 4, background:'var(--bg-3)', border:'1px solid var(--line)' }}>
          <span className="live-dot" style={{ width: 5, height: 5 }}/>
          <span className="mono" style={{ fontSize: 10, color:'var(--fg-3)' }}>{Number(a.uptime || 99.9).toFixed(1)}%</span>
        </span>
      </div>
      <p className="clamp-2" style={{ fontSize: 12.5, color:'var(--fg-3)', lineHeight: 1.55, margin:'0 0 14px', minHeight: 38 }}>{a.description || '—'}</p>
      <div className="row between" style={{ paddingTop: 12, borderTop:'1px solid var(--line)', color:'var(--fg-4)', fontSize: 11 }}>
        <div className="row gap-3">
          <span className="mono">{XPrim.fmt(a.hits || 0)}</span>
          <span style={{color:'var(--line-2)'}}>·</span>
          {/* Single headline number — AI 显 TTFT，非 AI 显 P50；后端 derive */}
          <span className="mono">{a.latency ? `${a.latency}ms` : '—'}</span>
        </div>
        <span style={{ color:'var(--fg-4)' }}>by {a.provider || (a.host || '').split('.')[0] || '—'}</span>
      </div>
    </div>
    );
  };
  const renderRow = (rowList, reverse) => (
    <div className="mask-edges" style={{ overflow:'hidden' }}>
      <div className={`marquee-row ${reverse ? 'reverse' : ''}`}>
        {rowList.map((a, i) => <Card key={`a-${a.id}-${i}`} a={a} k={`a-${a.id}-${i}`} />)}
        {rowList.map((a, i) => <Card key={`b-${a.id}-${i}`} a={a} k={`b-${a.id}-${i}`} />)}
      </div>
    </div>
  );
  return (
    <div className="col gap-4" style={{ paddingTop: 8 }}>
      {renderRow(row1, false)}
      {renderRow(row2, true)}
    </div>
  );
};

// ServiceCoverageVisual — terminal-styled live capability snapshot. Pulls real
// data from the action service and groups by `meta.category`. No hardcoded counts,
// no fake endpoint paths — if the backend is offline the panel shows "loading…"
// rather than guessing numbers.
const ServiceCoverageVisual = () => {
  const { t } = useT();
  const [groups, setGroups] = uS([]);
  const [total, setTotal] = uS(null);

  uE(() => {
    let cancelled = false;
    XApi.actionsService.list({ pageSize: 500 })
      .then(items => {
        if (cancelled) return;
        const byCat = new Map();
        for (const a of items) {
          const cat = (a.meta && a.meta.category) || (a.category) || 'Other';
          if (!byCat.has(cat)) byCat.set(cat, []);
          byCat.get(cat).push(a.id || a.displayName || '');
        }
        const sorted = Array.from(byCat.entries())
          .map(([category, ids]) => ({ category, count: ids.length, samples: ids.slice(0, 4) }))
          .sort((a, b) => b.count - a.count);
        setGroups(sorted);
        setTotal(items.length);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="term-panel" style={{ width:'100%', maxWidth: 620, marginLeft:'auto' }}>
      <div className="term-panel-head row between">
        <div className="row gap-2" style={{ alignItems:'center' }}>
          <span className="dot"/><span className="dot"/><span className="dot"/>
          <span className="micro-mono" style={{ marginLeft: 8, color:'var(--fg-3)', textTransform:'none' }}>xapi/services</span>
        </div>
        <span className="visual-live">
          <span className="live-dot"/>
          {total != null ? t('portal.section03.actionsCount').replace('{N}', total) : t('portal.section03.visualConnecting')}
        </span>
      </div>
      <div className="term-panel-body" style={{ padding: '20px 22px' }}>
        {/* Command line that produced the output below — sets the CLI mental model. */}
        <div className="mono" style={{ fontSize: 12.5, color: 'var(--fg-2)' }}>
          <span className="term-prompt">$</span>
          <span style={{ color: 'var(--fg-1)' }}>npx xapi-to list </span>
          <span style={{ color: 'var(--fg-4)' }}>--source capability --format table</span>
        </div>

        {/* Tabular header */}
        <div className="row mono" style={{
          marginTop: 16, paddingBottom: 8, fontSize: 11,
          color:'var(--fg-4)', letterSpacing:'0.08em',
          borderBottom: '1px dashed var(--line-2)',
        }}>
          <span style={{ flex: 1 }}>{t('portal.section03.colCategory')}</span>
          <span style={{ width: 90, textAlign: 'right' }}>{t('portal.section03.colActions')}</span>
        </div>

        {/* Body — each category lists count + a few sample action ids */}
        {groups.length === 0 && (
          <div className="mono" style={{ color:'var(--fg-4)', fontSize: 12, padding:'18px 0' }}>
            <span className="term-comment">{'// '}</span>{t('portal.section03.loading')}
          </div>
        )}
        {groups.map((g, i) => (
          <div key={g.category} style={{ marginTop: 14 }}>
            <div className="row between mono" style={{ alignItems:'baseline', fontSize: 13 }}>
              <span style={{ color:'var(--c-mint)' }}>{g.category}</span>
              <span style={{ color:'var(--fg-1)', width: 90, textAlign:'right' }}>{g.count}</span>
            </div>
            <div className="row gap-2" style={{ flexWrap:'wrap', marginTop: 6 }}>
              {g.samples.map(s => (
                <span key={s} className="mono" style={{
                  fontSize: 11, color:'var(--fg-3)', background:'var(--bg-2)',
                  padding:'3px 8px', borderRadius: 4, border:'1px solid var(--line)',
                }}>{s}</span>
              ))}
              {g.count > g.samples.length && (
                <span className="mono" style={{ fontSize: 11, color:'var(--fg-4)', padding:'3px 0' }}>
                  {t('portal.section03.more').replace('{N}', g.count - g.samples.length)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ PORTAL ============
const PortalPage = () => {
  const { navigate } = XShell.useRoute();
  const { authed } = XShell.useAuth();
  const { t } = useT();
  const [tool, setTool] = uS('claude');
  const [loaded, setLoaded] = uS(false);
  const [platform, setPlatform] = uS(null);
  const [topApis, setTopApis] = uS([]);
  React.useEffect(() => { setLoaded(true); }, []);

  // Real backend data:
  //   /statistics/platform   → hero stats (active nodes, total invocations, uptime, providers)
  //   /api-services          → marketplace list, take top 6 by hits/calls
  //   /statistics/api/batch/public?apiIds=… → uptime/latency per API for the cards
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [platSettled, listSettled] = await Promise.allSettled([
          XApi.statsService.platform(),
          XApi.apiSvc.listMarketplace(),
        ]);
        if (cancelled) return;
        if (platSettled.status === 'fulfilled' && platSettled.value) setPlatform(platSettled.value);

        const list = (listSettled.status === 'fulfilled' && Array.isArray(listSettled.value)) ? listSettled.value : [];
        if (!list.length) return;

        // Map to UI shape, take top 6. provider stays nullable here (display layer falls back
        // to host when there's no real provider) — used to default to '—' which leaked into
        // every card subtitle.
        const adapted = list.map(a => ({
          id: a.id,
          name: a.name,
          provider: a.provider || null,
          host: a.host || '',
          category: a.category || 'Data',
          description: a.description || '',
          logoUrl: a.logoUrl || '',
          hits: a.calls30d || 0,
          uptime: 99.9,
          latency: 0,
          verified: !!a.isPublic,
          tags: a.tags || [],
        })).slice(0, 6);

        // Try batch stats + timeseries in parallel — best effort.
        try {
          const ids = adapted.map(a => a.id).filter(Boolean);
          if (ids.length) {
            const [statsMap, tsMap] = await Promise.all([
              XApi.statsService.publicBatch ? XApi.statsService.publicBatch(ids) : Promise.resolve({}),
              XApi.statsService.timeseriesBatch ? XApi.statsService.timeseriesBatch(ids, 30) : Promise.resolve({}),
            ]);
            adapted.forEach(a => {
              const s = statsMap && statsMap[a.id];
              if (s) {
                a.uptime = s.uptime != null ? Number(s.uptime) : a.uptime;
                // 后端已 derive headlineLatency: AI=TTFT(avgUpstreamLatencyMs), 非 AI=p50。
                // 前端不再判类、不再算 fallback chain — backend 是唯一来源。
                a.latency = s.headlineLatency != null ? Math.round(Number(s.headlineLatency)) : null;
                a.latencyKind = s.headlineLatencyKind ?? 'P50';
                a.p95Latency  = s.p95Latency != null ? Math.round(Number(s.p95Latency)) : null;
                a.hits = s.totalHits != null ? Number(s.totalHits) : a.hits;
              }
              const ts = tsMap && tsMap[a.id];
              // Sparkline expects a flat numeric array. Empty/all-zero series → omit entirely.
              if (ts && Array.isArray(ts.points) && ts.points.length) {
                const series = ts.points.map(p => p.hits || 0);
                if (series.some(v => v > 0)) a.spark = series;
              }
            });
          }
        } catch {}
        if (!cancelled) setTopApis(adapted);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  // Live backend numbers. While the fetch is pending OR if it returns empty, render zeros instead of
  // bare em-dashes — a row of "—" reads as "broken page" to users, while "0" cleanly says "nothing yet".
  const p = platform || {};
  const stats = [
    { label: 'Active APIs',       value: XPrim.fmt(p.activeNodes ?? p.activeApis ?? 0) },
    { label: 'Total invocations', value: XPrim.fmt(p.totalInvocations ?? 0) },
    { label: 'Uptime avg',        value: p.uptimeAvg != null ? Number(p.uptimeAvg).toFixed(2) + '%' : '100.00%' },
    { label: 'Total users',       value: XPrim.fmt(p.totalUsers ?? 0) },
  ];

  const toolConfigs = {
    claude: {
      label: '~/.claude/mcp.json',
      code: `{
  "mcpServers": {
    "xapi": {
      "url": "https://mcp.xapi.to",
      "headers": {
        "Authorization": "Bearer sk-8f3a****a92f"
      }
    }
  }
}`,
    },
    cursor: {
      label: '~/.cursor/settings.json',
      code: `{
  "mcp.servers": {
    "xapi": "https://mcp.xapi.to?token=sk-8f3a****a92f"
  }
}`,
    },
    sdk: {
      label: 'npm install @xapi/client',
      code: `import { xapi } from '@xapi/client';

const client = xapi({ apiKey: process.env.XAPI_KEY });

// any marketplace API, one client
const weather = await client.call('weather-pro/forecast', { lat, lon });
const llm     = await client.call('llm-gateway/chat', { messages });`,
    },
    curl: {
      label: 'any language, one gateway',
      code: `curl https://llm.p.xapi.to/v1/chat/completions \\
  -H "Authorization: Bearer sk-8f3a****a92f" \\
  -H "Content-Type: application/json" \\
  -d '{ "model":"claude-haiku-4.5", "messages":[...] }'`,
    },
  };

  // Real backend top APIs — empty array means "no published APIs yet".
  const top = topApis;

  // Raw numeric inputs for the count-up ribbon. Strings ("100.00%", em-dashes) come back
  // unchanged via the formatter passed to <Counter>.
  const platformRaw = {
    activeApis:       Number(p.activeNodes ?? p.activeApis ?? 0),
    totalInvocations: Number(p.totalInvocations ?? 0),
    uptimeAvg:        p.uptimeAvg != null ? Number(p.uptimeAvg) : 100,
    totalUsers:       Number(p.totalUsers ?? 0),
    avgLatency:       p.avgLatency != null ? Number(p.avgLatency) : 0,
  };

  return (
    <div>
      {/* HERO. Editorial: massive title with mono "//" marker, kicker tag, body
          paragraph floating right of the title block, mint primary CTA on the
          left, ghost CTA next to it. Right side hosts a real-feeling terminal
          panel (not a fake docs card) — the page's first ASCII moment.

          Hero is intentionally compact (~62vh) so that the marketplace marquee
          immediately below lands inside the first viewport. The old fill-the-
          whole-screen sizing was paired with a 96px metric strip; both are gone. */}
      <section className="hero-section" style={{ position:'relative', overflow:'hidden', background:'var(--bg-0)', minHeight:'calc(66vh - 52px)', display:'flex', flexDirection:'column' }}>
        <div className="grid-bg" style={{
          position:'absolute', inset:0, opacity:0.18,
          maskImage:'linear-gradient(180deg, #000 0%, transparent 100%)',
          WebkitMaskImage:'linear-gradient(180deg, #000 0%, transparent 100%)',
        }} />
        <div className="hero-blob" style={{ width: 540, height: 540, left:'-10%', top:'-20%', opacity: 0.35, background:'radial-gradient(circle, color-mix(in oklab, var(--c-mint) 22%, transparent) 0%, transparent 60%)' }} />

        <div className="hero-eyebrow" style={{ maxWidth: 1480, margin:'0 auto', padding:'56px 40px 20px', position:'relative', width:'100%' }}>
          <div className={`reveal ${loaded ? 'is-revealed' : ''}`} style={{ display:'flex', alignItems:'center', gap: 12, color:'var(--fg-3)', fontFamily:'var(--font-mono)', fontSize: 12, letterSpacing:'0.12em', textTransform:'uppercase' }}>
            <span className="live-dot"/> <span style={{color:'var(--c-mint)'}}>xapi</span> <span style={{color:'var(--fg-4)'}}>//</span> <span>{t('portal.kicker')}</span>
          </div>
        </div>

        <div className="hero-grid" style={{ maxWidth: 1480, margin:'0 auto', padding:'24px 40px 48px', position:'relative', display:'grid', gridTemplateColumns:'1.15fr 0.85fr', gap: 56, alignItems:'center', flex: 1, width:'100%' }}>
          <div>
            <h1 className={`reveal delay-1 ${loaded ? 'is-revealed' : ''}`} style={{
              margin: 0,
              fontSize: 'clamp(40px, 5.2vw, 76px)',
              lineHeight: 0.98,
              letterSpacing: '-0.045em',
              fontWeight: 600,
              color: 'var(--fg-0)',
              // word-break: keep-all forbids char-level breaks inside CJK runs, so
              // "代理的" stays whole instead of splitting "代理 / 的". Latin text is
              // unaffected — it still breaks at spaces.
              wordBreak: 'keep-all',
            }}>
              {/* Balancer uses Knuth's optimal line-breaking — handles CJK + Latin
                  mixing better than browser-native `text-wrap: balance`, which
                  doesn't understand that "代理的 API" should stay together. */}
              <Balancer>
                {t('portal.hero.title').split(' ').slice(0, -1).join(' ')}{' '}
                <span className="slash-mark">//</span>{' '}
                <span className="mint-text">{t('portal.hero.title').split(' ').slice(-1)[0]}</span>
              </Balancer>
            </h1>

            <div className="row gap-6" style={{ marginTop: 28, flexWrap:'wrap' }}>
              <p className={`reveal delay-2 ${loaded ? 'is-revealed' : ''} hero-desc-desktop`} style={{ margin: 0, fontSize: 16, color:'var(--fg-2)', maxWidth: 520, lineHeight: 1.6 }}>
                <Balancer>{t('portal.hero.aDesc')}</Balancer>
              </p>
              <p className={`reveal delay-2 ${loaded ? 'is-revealed' : ''} hero-desc-mobile`} style={{ margin: 0, fontSize: 15, color:'var(--fg-2)', maxWidth: 520, lineHeight: 1.55 }}>
                {/* No <Balancer> here — for short mobile copy, balancing forces an
                    early break (e.g. "搜索、" is bumped to a new line just to
                    even the two line-widths) when there's plenty of room left.
                    Let the browser flow it naturally. */}
                {t('portal.hero.descriptionMobile')}
              </p>
              <div className={`reveal delay-2 ${loaded ? 'is-revealed' : ''}`} style={{ display:'flex', alignItems:'flex-start', gap: 8 }}>
                <span className="ascii-rule" style={{ writingMode:'vertical-rl', height: 60, transform:'rotate(180deg)' }}>{'·'.repeat(20)}</span>
              </div>
            </div>

            <div className={`reveal delay-3 ${loaded ? 'is-revealed' : ''} row gap-3`} style={{ marginTop: 28 }}>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('market')}>
                <span className="arrow">→</span> {t('portal.hero.exploreApis')}
              </button>
              <button className="btn btn-lg" onClick={() => navigate('workspace')}>
                <XIcons.IconRocket size={14}/> {t('portal.hero.becomeProvider')}
              </button>
            </div>
          </div>

          <HeroTabTerminal loaded={loaded}/>
        </div>
      </section>

      {/* ── Version-A：数据条 + 4 业务大块（hero 与市场之间）。同底色 + 同款网格纹理保持背景连续、
          过渡平滑；顶部/底部各一条细分割线把这块框住；宽度对齐 hero(1480)，数据条满宽、与 4 块左右对齐。 ── */}
      <section style={{ position:'relative', overflow:'hidden', background:'var(--bg-0)', borderTop:'1px solid var(--line)', borderBottom:'1px solid var(--line)' }}>
        <div className="grid-bg" style={{
          position:'absolute', inset:0, opacity:0.10,
          maskImage:'linear-gradient(180deg, transparent 0%, #000 24%, #000 76%, transparent 100%)',
          WebkitMaskImage:'linear-gradient(180deg, transparent 0%, #000 24%, #000 76%, transparent 100%)',
        }} />
        <div style={{ maxWidth: 1480, margin:'0 auto', padding:'clamp(52px, 6vw, 76px) 40px', position:'relative', zIndex: 1 }}>
          {/* 数据条 —— /statistics/platform 真实数据。数据可信前先隐藏，恢复时把 false 改成 true。 */}
          {false && (
          <div className="home-stats">
            <div className="hstat"><div className="hstat-n">{fmtStat(platform?.totalInvocations, '+')}</div><div className="hstat-l">{t('portal.feat.stat.calls')}</div></div>
            <div className="hstat"><div className="hstat-n">{fmtStat(platform?.activeNodes, '+')}</div><div className="hstat-l">{t('portal.feat.stat.apis')}</div></div>
            <div className="hstat"><div className="hstat-n">{fmtStat(platform?.activeProviders)}</div><div className="hstat-l">{t('portal.feat.stat.providers')}</div></div>
            <div className="hstat"><div className="hstat-n">{platform?.uptimeAvg != null ? Number(platform.uptimeAvg).toFixed(1) : '99.9'}<span className="u">%</span></div><div className="hstat-l">{t('portal.feat.stat.uptime')}</div></div>
          </div>
          )}

          {/* 4 大块 —— 整块可点击（键盘可达）；内部 CTA 降级为视觉 span */}
          <div className="home-blocks">
            {/* 调用方 */}
            <ClickableBlock className="home-block accent" onGo={() => navigate(authed ? 'console' : 'login')}>
              <div className="hb-body">
                <span className="hb-chip consumer"><XIcons.IconLayers size={12}/> {t('portal.feat.b1.chip')}</span>
                <h3>{t('portal.feat.b1.title')}</h3>
                <p>{t('portal.feat.b1.desc')}</p>
                <span className="hb-link">{t('portal.feat.b1.cta')} <span className="arrow">→</span></span>
              </div>
            </ClickableBlock>
            {/* 服务商 */}
            <ClickableBlock className="home-block accent" onGo={() => navigate('workspace')}>
              <div className="hb-body">
                <span className="hb-chip provider"><XIcons.IconWallet size={12}/> {t('portal.feat.b2.chip')}</span>
                <h3>{t('portal.feat.b2.title')}</h3>
                <p>{t('portal.feat.b2.desc')}</p>
                <span className="hb-link is-mint">{t('portal.feat.b2.cta')} <span className="arrow">→</span></span>
              </div>
            </ClickableBlock>
            {/* Skills */}
            <ClickableBlock className="home-block" onGo={() => navigate('skill')}>
              <div className="hb-body">
                <span className="hb-chip"><XIcons.IconTerminal size={12}/> Skill · MCP</span>
                <h3>{t('portal.feat.b3.title')}</h3>
                <p>{t('portal.feat.b3.desc')}</p>
                <span className="hb-link">{t('portal.feat.b3.cta')} <span className="arrow">→</span></span>
              </div>
            </ClickableBlock>
            {/* AI */}
            <ClickableBlock className="home-block" onGo={() => navigate('ai')}>
              <div className="hb-body">
                <span className="hb-chip"><XIcons.IconZap size={12}/> {t('portal.feat.b4.chip')}</span>
                <h3>{t('portal.feat.b4.title')}</h3>
                <p>{t('portal.feat.b4.desc')}</p>
                <span className="hb-link">{t('portal.feat.b4.cta')} <span className="arrow">→</span></span>
              </div>
            </ClickableBlock>
          </div>
        </div>
      </section>

      {/* SECTION 01 — Marketplace marquee. Two opposite-direction infinite rows of
          API cards with edge fade. Promoted into the first-viewport slot directly
          under the hero — the marketplace is the goods, so it leads. Tighter
          vertical padding so the title row peeks above the fold on a typical
          1080p viewport while the cards live just below. */}
      <RevealSection className="sec-pad-tight section-seam">
        <div className="reveal-stagger" style={{ maxWidth: 1480, margin:'0 auto', padding:'0 40px 24px' }}>
          <div className="row between" style={{ alignItems:'baseline', flexWrap:'wrap', gap: 16 }}>
            <div>
              <div className="sec-num"><span className="grey">01</span> &nbsp;{t('portal.section01.eyebrow')} <span style={{color:'var(--fg-4)'}}>//</span> <span className="mint-text">{t('portal.section01.live').replace('{N}', XPrim.fmt(top.length))}</span></div>
              <h2 style={{ margin:'12px 0 0', fontSize:'clamp(32px, 4.2vw, 52px)', letterSpacing:'-0.035em', fontWeight: 600, lineHeight: 1.02, textWrap:'balance' }}>
                {t('portal.section01.titleA')} <span className="slash-mark">//</span> <span style={{color:'var(--fg-3)'}}>{t('portal.section01.titleB')}</span>
              </h2>
            </div>
            <button className="btn btn-sm" onClick={() => navigate('market')}>
              {t('portal.section01.viewAll')} <XIcons.IconArrowRight size={12}/>
            </button>
          </div>
        </div>

        {top.length === 0 ? (
          <div style={{ maxWidth: 1480, margin:'0 auto', padding:'0 40px' }}>
            <div className="card" style={{padding: 48, textAlign:'center', border:'1px dashed var(--line)', background:'transparent'}}>
              <div className="micro" style={{color:'var(--fg-4)', marginBottom: 10}}>{t('portal.section01.empty.title')}</div>
              <p style={{color:'var(--fg-3)', fontSize: 13, lineHeight: 1.7, maxWidth: 480, margin:'0 auto'}}>
                {t('portal.section01.empty.body')}
              </p>
              <button className="btn btn-primary" style={{marginTop: 16}} onClick={()=>navigate('workspace-new')}>
                <span className="arrow">→</span> {t('portal.section01.empty.register')}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ maxWidth: 1480, margin:'0 auto', padding:'0 40px' }}>
            <ApiMarquee items={top} navigate={navigate} />
          </div>
        )}
      </RevealSection>

      {/* SECTION 02 — One key, every API. Three-column animated bridge:
          left = AI agents (Claude / Cursor / Codex), middle = xapito hub,
          right = a fan-out of provider APIs. Pulses fire on random
          agent↔hub and hub↔API pairs to telegraph live routing. */}
      <RevealSection className="sec-pad-regular section-seam" style={{ background:'var(--bg-0)' }}>
        <div className="reveal-stagger" style={{ maxWidth: 1480, margin:'0 auto', padding:'0 40px' }}>
          {/* Title block — centered to match the centered diagram beneath it,
              and constrained to roughly the diagram's width so the heading
              and the visual share one strong vertical axis. */}
          <div style={{ textAlign: 'center', maxWidth: 880, margin: '0 auto 56px' }}>
            <div className="sec-num" style={{ justifyContent: 'center' }}>
              <span className="grey">02</span> &nbsp;{t('portal.section02.eyebrow')}
            </div>
            <h2 style={{ margin:'14px 0 0', fontSize:'clamp(30px, 4vw, 46px)', letterSpacing:'-0.035em', fontWeight: 600, lineHeight: 1.05, textWrap:'balance' }}>
              {t('portal.section02.titleA')} <span className="slash-mark">//</span> <span style={{color:'var(--fg-3)'}}>{t('portal.section02.titleB')}</span>
            </h2>
          </div>

          <BridgeVisual />

          {/* Fact strip — three muted pills under the bridge so the section
              doesn't read as just "diagram floating in space" now that the
              border around the visual is gone. */}
          <div className="bridge-facts">
            <span className="bridge-fact">
              <span className="live-dot"/> <span className="mint-text">{t('portal.cap.01.fact.tools')}</span>
            </span>
            <span className="bridge-fact-sep" aria-hidden="true">·</span>
            <span className="bridge-fact">
              {t('portal.cap.02.fact.head')} <span className="mono mint-text">~{Math.round(platformRaw.avgLatency || 30)}ms</span>
            </span>
            <span className="bridge-fact-sep" aria-hidden="true">·</span>
            <span className="bridge-fact">
              <span className="mint-text">{t('portal.cap.04.fact.share')}</span>
            </span>
          </div>
        </div>
      </RevealSection>

      {/* SECTION 03 — Action coverage visual. Live grouped list of every action
          we expose, served as a typed terminal stream + summary list. */}
      <RevealSection className="sec-pad-regular section-seam">
        <div className="visual-section split reveal-stagger" style={{ maxWidth: 1480, margin:'0 auto', padding:'0 40px' }}>
          <div className="visual-copy">
            <div className="sec-num"><span className="grey">03</span> &nbsp;{t('portal.section03.eyebrow')}</div>
            <h2 className="visual-title">
              {t('portal.section03.titleA')} <span className="slash-mark">//</span> <span style={{color:'var(--fg-3)'}}>{t('portal.section03.titleB')}</span>
            </h2>
            <p className="visual-summary">{t('portal.section03.summary')}</p>
            <div className="visual-points compact">
              <span>{t('portal.section03.point1')}</span>
              <span>{t('portal.section03.point2')}</span>
              <span>{t('portal.section03.point3')}</span>
            </div>
          </div>
          <ServiceCoverageVisual />
        </div>
      </RevealSection>

      {/* SECTION 04 — Get started. Two-step onboarding (install + use) plus
          collapsible CLI install and updates blocks. Sits after the coverage
          visual so users go capability inventory → install → call. */}
      <GetStartedSection navigate={navigate}/>

      {/* Final CTA strip — single horizontal band with two side-by-side actions
          (consumer / provider). Wrapped in RevealSection so it fades up on
          scroll like the rest of the page, instead of being the only static
          block at the bottom. */}
      <RevealSection className="sec-pad-compact section-seam">
        <div className="reveal-stagger" style={{ maxWidth: 1480, margin:'0 auto', padding:'0 40px' }}>
          <div className="row between cta-strip" style={{ alignItems:'center', flexWrap:'wrap', gap: 24 }}>
            <div className="cta-strip-text" style={{ minWidth: 0, flex: 1 }}>
              <div className="sec-num"><span className="grey">→</span> &nbsp;{t('portal.cta.eyebrow')}</div>
              <h2 style={{ margin:'10px 0 0', fontSize:'clamp(28px, 3.4vw, 40px)', letterSpacing:'-0.03em', fontWeight: 600, lineHeight: 1.1, textWrap:'balance' }}>
                {t('portal.cta.titleA')} <span className="slash-mark">//</span> <span style={{color:'var(--fg-3)'}}>{t('portal.cta.titleB')}</span>
              </h2>
            </div>
            <div className="row gap-3 cta-strip-actions" style={{ flexShrink: 0 }}>
              <button className="btn btn-primary btn-lg" onClick={() => navigate(authed ? 'console' : 'login')}>
                <span className="arrow">→</span> {authed ? t('auth.dashboard') : t('auth.getApiKey')}
              </button>
              <button className="btn btn-lg" onClick={() => navigate('workspace-new')}>
                <XIcons.IconPlus size={13}/> {t('portal.cta.register')}
              </button>
            </div>
          </div>
        </div>
      </RevealSection>

      <Footer />
    </div>
  );
};

// ApiAvatar — 卡片左上角图标。优先用 service.logoUrl 渲染图片，加载失败或缺失则
// 回落字母头像（首字母 + 分类色背景）。统一被 marquee / grid / table 三处复用，
// 避免每个卡片各自重复实现导致样式漂移。
//
// 注：图片背景用中性深色（var(--bg-2)），不沿用分类 tint —— 真实 logo 已经自带品牌色,
// 再叠加分类色会冲突。字母头像保留分类 tint，否则纯字母会显得干瘪。
export const ApiAvatar = ({
  name, logoUrl, meta = {},
  size = 38, radius = 8, fontSize = 15,
}) => {
  const [errored, setErrored] = uS(false);
  const useImage = !!logoUrl && !errored;
  const baseStyle = {
    width: size, height: size, borderRadius: radius,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  };
  if (useImage) {
    // 透明背景 + 无 border，logo 直接坐在卡片表面。
    // 72% 尺寸保留四周留白，与字母头像视觉重量一致
    return (
      <div style={{
        ...baseStyle,
        background: 'transparent',
      }}>
        <img
          src={logoUrl}
          alt={name || ''}
          onError={() => setErrored(true)}
          style={{
            width: '72%', height: '72%',
            objectFit: 'contain', display: 'block',
          }}
        />
      </div>
    );
  }
  return (
    <div style={{
      ...baseStyle,
      background: meta.hue != null ? meta.bg : 'var(--bg-3)',
      border: meta.hue != null ? `1px solid ${meta.border}` : '1px solid var(--line)',
      color: meta.hue != null ? meta.fg : 'var(--fg-1)',
      fontFamily: 'var(--font-mono)', fontSize, fontWeight: 600,
    }}>
      {(name || '?').charAt(0)}
    </div>
  );
};

// Reveal-on-scroll <section> wrapper. Uses useReveal + the `.reveal` CSS class so the
// section fades + lifts in once it crosses the 12% viewport threshold. Otherwise behaves
// identically to a plain <section>.
const RevealSection = ({ children, ...props }) => {
  const ref = useReveal();
  return (
    <section ref={ref} className={`reveal ${props.className || ''}`} style={props.style}>
      {children}
    </section>
  );
};

export const Footer = () => {
  const { t } = useT();
  const linkStyle = {
    color: 'var(--fg-3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 32, height: 32, borderRadius: 6, textDecoration: 'none', transition: 'color .15s, background .15s',
  };
  return (
    <footer style={{borderTop:'1px solid var(--line)', padding:'24px clamp(16px, 4vw, 40px)'}}>
      <div style={{maxWidth: 1480, margin:'0 auto'}} className="row between footer-row">
        <span style={{fontSize: 12, color: 'var(--fg-3)'}}>{t('portal.footer.copyright')}</span>
        <div className="row gap-1">
          <a href="https://x.com/xapi_to" target="_blank" rel="noreferrer noopener" aria-label={t('portal.footer.twitter')} className="footer-icon" style={linkStyle}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </a>
          <a href="https://t.me/+mapOl6ayUYw3YWNl" target="_blank" rel="noreferrer noopener" aria-label={t('portal.footer.telegram')} className="footer-icon" style={linkStyle}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.7L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
            </svg>
          </a>
          <a href="https://github.com/xapi-labs" target="_blank" rel="noreferrer noopener" aria-label={t('portal.footer.github')} className="footer-icon" style={linkStyle}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.11.79-.25.79-.56v-1.96c-3.2.7-3.87-1.54-3.87-1.54-.52-1.32-1.27-1.67-1.27-1.67-1.04-.71.08-.7.08-.7 1.15.08 1.76 1.18 1.76 1.18 1.02 1.75 2.68 1.24 3.34.95.1-.74.4-1.24.72-1.53-2.55-.29-5.24-1.27-5.24-5.66 0-1.25.45-2.27 1.18-3.07-.12-.29-.51-1.46.11-3.04 0 0 .96-.31 3.15 1.17a10.95 10.95 0 015.74 0c2.19-1.48 3.15-1.17 3.15-1.17.62 1.58.23 2.75.11 3.04.74.8 1.18 1.82 1.18 3.07 0 4.4-2.69 5.36-5.25 5.65.41.35.78 1.05.78 2.12v3.14c0 .31.21.68.8.56 4.56-1.52 7.85-5.83 7.85-10.91C23.5 5.65 18.35.5 12 .5z"/>
            </svg>
          </a>
        </div>
      </div>
    </footer>
  );
};

// ============ MARKET ============
const MarketPage = () => {
  const { navigate } = XShell.useRoute();
  const { authed } = XShell.useAuth();
  const { t } = useT();
  const [cat, setCat] = uS('All');
  const [q, setQ] = uS('');
  const [view, setView] = uS('grid');
  const [sort, setSort] = uS('popular');
  // Sidebar visibility — defaults open on desktop; mobile uses the bottom sheet
  // controlled by `filtersOpen` instead. CSS handles the breakpoint switch.
  const [sideOpen, setSideOpen] = uS(true);
  const [filtersOpen, setFiltersOpen] = uS(false);

  // Lock body scroll while the mobile filter sheet is open so a long category
  // list inside the sheet doesn't scroll the underlying page when you reach
  // its end.
  uE(() => {
    if (!filtersOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [filtersOpen]);

  // Real data sources:
  //   /api-services               → marketplace list (public)
  //   /statistics/api/batch/public → uptime/latency/hits per API (public, chunked to 50)
  //   /api-services/favorites      → user's bookmarks (JWT, best-effort)
  const [apis, setApis] = uS([]);
  const [loading, setLoading] = uS(true);
  const [favSet, setFavSet] = uS(new Set());
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [listR, favR] = await Promise.allSettled([
          XApi.apiSvc.listMarketplace(),
          XApi.favoriteService.list(),
        ]);
        if (cancelled) return;
        const list = (listR.status === 'fulfilled' && Array.isArray(listR.value)) ? listR.value : [];
        const favIds = (favR.status === 'fulfilled' && favR.value && Array.isArray(favR.value.items))
          ? favR.value.items.map(x => x.id || x.apiServiceId).filter(Boolean)
          : [];
        setFavSet(new Set(favIds));
        if (!list.length) { setApis([]); return; }

        const mapped = list.map(a => ({
          id: a.id,
          slug: a.host || a.slug || a.id,
          name: a.name,
          // Same as PortalPage — keep null when backend has no provider so the display layer
          // can fall back to the hostname instead of rendering a bare em-dash.
          provider: a.provider || null,
          host: XApi.helpers.gatewayHost(a.host, a.integrationMode),
          category: a.category || 'Data',
          description: a.description || '',
          logoUrl: a.logoUrl || '',
          hits: a.calls30d || 0,
          stars: a.stars || 0,
          uptime: a.uptime != null ? Number(a.uptime) : 99.9,
          latency: a.latency != null ? Number(a.latency) : 0,
          status: (a.status || 'ACTIVE').toLowerCase(),
          verified: !!a.isPublic,
          billing: 'per-call',
          tags: a.tags || [],
          endpoints: (a.endpoints || []).map(ep => ({
            id: ep.id, method: ep.method, path: ep.path, name: ep.name || ep.path,
            cost: endpointListCost(ep),
            desc: ep.description || '',
          })),
        }));

        // Enrich with public batch stats + sparkline series in parallel — best effort.
        try {
          const ids = mapped.map(m => m.id).filter(Boolean);
          const [statsMap, tsMap] = await Promise.all([
            XApi.statsService.publicBatch(ids),
            XApi.statsService.timeseriesBatch ? XApi.statsService.timeseriesBatch(ids, 30) : Promise.resolve({}),
          ]);
          mapped.forEach(m => {
            const s = statsMap && statsMap[m.id];
            if (s) {
              if (s.uptime != null) m.uptime = Number(s.uptime);
              // 同卡片视图：用后端 derive 的 headlineLatency（AI=TTFT, 非 AI=p50）
              m.latency = s.headlineLatency != null ? Math.round(Number(s.headlineLatency)) : null;
              m.latencyKind = s.headlineLatencyKind ?? 'P50';
              m.p95Latency = s.p95Latency != null ? Math.round(Number(s.p95Latency)) : null;
              if (s.totalHits != null) m.hits = Number(s.totalHits);
            }
            const ts = tsMap && tsMap[m.id];
            if (ts && Array.isArray(ts.points) && ts.points.length) {
              const series = ts.points.map(p => p.hits || 0);
              if (series.some(v => v > 0)) m.spark = series;
            }
          });
        } catch {}

        if (!cancelled) setApis(mapped);
      } catch { /* swallow — empty state will handle */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleFavorite = async (apiId, currentlyFav) => {
    const next = new Set(favSet);
    if (currentlyFav) { next.delete(apiId); } else { next.add(apiId); }
    setFavSet(next);
    try {
      if (currentlyFav) await XApi.favoriteService.remove(apiId);
      else await XApi.favoriteService.add(apiId);
    } catch {
      // Revert on failure.
      setFavSet(favSet);
    }
  };

  const list = uM(() => {
    let l = apis.filter(a => cat === 'All' || a.category === cat);
    if (q) l = l.filter(a => (a.name + a.description + (a.tags||[]).join(' ')).toLowerCase().includes(q.toLowerCase()));
    if (sort === 'popular') l = [...l].sort((a,b) => (b.hits||0) - (a.hits||0));
    if (sort === 'new') l = [...l].reverse();
    if (sort === 'latency') l = [...l].sort((a,b) => (a.latency||0) - (b.latency||0));
    return l;
  }, [cat, q, sort, apis]);

  // Sidebar category list — derived from the actual API data so it always reflects
  // what the backend ships. Known categories sort first (in CATEGORIES order); any
  // unknown category found in the data appears below alphabetically.
  const sidebarCategories = uM(() => {
    const present = new Set(apis.map(a => a.category).filter(Boolean));
    const known = XData.CATEGORIES.filter(c => c === 'All' || present.has(c));
    const unknown = [...present].filter(c => !XData.CATEGORIES.includes(c)).sort();
    return [...known, ...unknown];
  }, [apis]);

  // Filter content shared between desktop sidebar and mobile bottom sheet. Pulling
  // it into one block keeps the two presentations in sync — same checkboxes, same
  // category counts — instead of duplicating the markup.
  const FilterContent = () => (
    <>
      <div className="micro" style={{marginBottom: 10}}>{t('market.category')}</div>
      <div className="col gap-1">
        {sidebarCategories.map(c => {
          const meta = XPrim.categoryMeta(c);
          const isActive = cat === c;
          return (
            <button key={c} onClick={()=>setCat(c)} className={`nav-item ${isActive?'active':''}`}>
              <span className="row gap-2" style={{ alignItems:'center' }}>
                {meta.hue != null && (
                  <span style={{
                    width: 6, height: 6, borderRadius:'50%',
                    background: meta.dot,
                    boxShadow: isActive ? `0 0 6px ${meta.dot}` : 'none',
                  }}/>
                )}
                <span>{XPrim.categoryLabel(t, c)}</span>
              </span>
              <span className="num">{c === 'All' ? apis.length : apis.filter(a=>a.category===c).length}</span>
            </button>
          );
        })}
      </div>
      <div className="micro" style={{marginTop: 28, marginBottom: 10}}>{t('market.billing')}</div>
      <div className="col gap-1">
        <label className="nav-item"><input type="checkbox" defaultChecked style={{accentColor:'#fff'}}/> {t('market.billing.perCall')}</label>
        <label className="nav-item"><input type="checkbox" defaultChecked style={{accentColor:'#fff'}}/> {t('market.billing.perToken')}</label>
        <label className="nav-item"><input type="checkbox" defaultChecked style={{accentColor:'#fff'}}/> {t('market.billing.freeTier')}</label>
      </div>
      <div className="micro" style={{marginTop: 28, marginBottom: 10}}>{t('market.status')}</div>
      <div className="col gap-1">
        <label className="nav-item"><input type="checkbox" defaultChecked style={{accentColor:'#fff'}}/> {t('market.verifiedOnly')}</label>
      </div>
    </>
  );

  return (
    <div>
      <div style={{borderBottom:'1px solid var(--line)'}}>
        <div className="market-header-inner" style={{maxWidth: 1480, margin: '0 auto', padding: '32px 40px 24px'}}>
          <div className="micro">{t('market.eyebrow')}</div>
          <h1 style={{margin: '6px 0 0', fontSize: 32, letterSpacing:'-0.02em', fontWeight: 500}}>{t('market.title')}</h1>
          {/* Compact toolbar — single row of: filter icon · search input · view
              toggle · sort. On mobile the filter icon opens the bottom sheet,
              the search input takes the remaining space, and view/sort stay
              inline so the whole bar fits without wrapping. */}
          <div className="row market-toolbar" style={{marginTop: 20, gap: 8, alignItems:'center'}}>
            {/* Filter icon — opens sidebar (desktop, when collapsed) or bottom sheet (mobile). */}
            <button
              className="btn btn-sm btn-icon market-filter-icon"
              onClick={() => {
                if (window.matchMedia('(max-width: 820px)').matches) setFiltersOpen(true);
                else setSideOpen(o => !o);
              }}
              style={{ height: 36, width: 36, flexShrink: 0 }}
              title={sideOpen ? t('market.hideFilters') : t('market.showFilters')}
              aria-label={t('market.filters')}
            >
              <XIcons.IconFilter size={13}/>
            </button>

            {/* Search — flex-grows to fill the row */}
            <div className="hairline row gap-2" style={{padding:'0 12px', height: 36, borderRadius: 6, background:'var(--bg-1)', flex: 1, minWidth: 0}}>
              <XIcons.IconSearch size={14} style={{flexShrink: 0}}/>
              <input value={q} onChange={e=>setQ(e.target.value)} placeholder={t('market.searchPlaceholder')}
                style={{flex:1, background:'transparent', border:'none', outline:'none', fontSize: 13, minWidth: 0}}/>
            </div>

            {/* View toggle — grid / list. Always visible, compact. */}
            <div className="row hairline" style={{borderRadius: 6, overflow:'hidden', background:'var(--bg-1)', flexShrink: 0}}>
              <button onClick={()=>setView('grid')} style={{padding:'0 10px', height: 36, background: view==='grid' ? 'var(--bg-3)':'transparent', border:'none', color:'inherit', cursor:'pointer'}} aria-label="Grid view"><XIcons.IconGrid size={13}/></button>
              <button onClick={()=>setView('list')} style={{padding:'0 10px', height: 36, background: view==='list' ? 'var(--bg-3)':'transparent', border:'none', color:'inherit', cursor:'pointer'}} aria-label="List view"><XIcons.IconList size={13}/></button>
            </div>

            {/* Sort — compact on mobile (no label, just current value); full on desktop */}
            <Select value={sort} onChange={setSort}
              triggerStyle={{height: 36, background:'var(--bg-1)', flexShrink: 0}}
              triggerClassName="market-sort-trigger"
              options={[
                { value: 'popular', label: t('market.sort.popular') },
                { value: 'new',     label: t('market.sort.new') },
                { value: 'latency', label: t('market.sort.latency') },
              ]}/>
          </div>
        </div>
      </div>

      <div
        className={`market-grid market-grid-padded ${sideOpen ? '' : 'is-collapsed'}`}
        style={{
          maxWidth: 1480, margin: '0 auto', display:'grid',
          gridTemplateColumns: sideOpen ? '220px 1fr' : '1fr',
          gap: 40, padding:'24px 40px 80px',
        }}
      >
        {sideOpen && (
          <aside className="market-sidebar">
            <div className="row between" style={{ marginBottom: 4, alignItems:'center' }}>
              <div className="micro">{t('market.filters')}</div>
              <button
                onClick={() => setSideOpen(false)}
                className="btn btn-sm btn-ghost btn-icon"
                title={t('market.hideFilters')}
                aria-label={t('market.hideFilters')}
              >
                <XIcons.IconX size={11}/>
              </button>
            </div>
            <FilterContent/>
          </aside>
        )}

        <main>
          <div className="row between" style={{marginBottom: 16}}>
            <div className="mono" style={{fontSize: 12, color: 'var(--fg-3)'}}>
              {loading ? t('common.loading') : `${list.length} ${t('market.results')}${cat !== 'All' ? ' · ' + cat : ''}`}
            </div>
          </div>

          {loading && (
            // Skeleton grid — 6 placeholder cards that mirror the real card layout (icon,
            // name line, provider line, two description lines, tag pills, footer metrics).
            // Reads as "content is on the way" rather than the previous spinner which felt
            // like the page was stuck.
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))', gap: 12}}>
              {Array.from({length: 6}).map((_, i) => (
                <div key={i} className="card" style={{padding: 18}}>
                  <div className="row gap-2" style={{alignItems:'center'}}>
                    <span className="skeleton" style={{width: 28, height: 28, borderRadius: 6, flexShrink: 0}} />
                    <div style={{flex: 1, minWidth: 0}}>
                      <span className="skeleton" style={{width: '55%', height: 12, marginBottom: 6}} />
                      <span className="skeleton" style={{width: '35%', height: 9, display:'block'}} />
                    </div>
                  </div>
                  <span className="skeleton" style={{width: '100%', height: 10, marginTop: 14, display:'block'}} />
                  <span className="skeleton" style={{width: '85%', height: 10, marginTop: 6, display:'block'}} />
                  <div className="row gap-2" style={{marginTop: 14}}>
                    <span className="skeleton" style={{width: 38, height: 16, borderRadius: 9}} />
                    <span className="skeleton" style={{width: 50, height: 16, borderRadius: 9}} />
                    <span className="skeleton" style={{width: 32, height: 16, borderRadius: 9}} />
                  </div>
                  <div className="row between" style={{marginTop: 14}}>
                    <span className="skeleton" style={{width: '50%', height: 10}} />
                    <span className="skeleton" style={{width: 56, height: 18}} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && apis.length === 0 && (
            <div className="card" style={{padding: 60, textAlign:'center', border:'1px dashed var(--line)', background:'transparent'}}>
              <XIcons.IconLayers size={28} style={{color:'var(--fg-4)'}}/>
              <h3 style={{margin:'12px 0 8px', fontSize: 18, fontWeight: 500}}>{t('market.empty.title')}</h3>
              <p style={{color:'var(--fg-3)', fontSize: 13, lineHeight: 1.7, maxWidth: 440, margin:'0 auto 16px'}}>
                {t('market.empty.body')}
              </p>
              <button className="btn btn-primary" onClick={()=>navigate('workspace-new')}><XIcons.IconPlus size={13}/> {t('market.empty.register')}</button>
            </div>
          )}

          {!loading && apis.length > 0 && list.length === 0 && (
            <div className="card" style={{padding: 48, textAlign:'center', color:'var(--fg-3)', fontSize: 13}}>
              {t('market.noMatch')}
            </div>
          )}

          {!loading && list.length > 0 && (view === 'grid' ? (
            // apx-stagger-in plays once on mount (after the skeleton clears);
            // filter/search typing doesn't remount the grid, so it never replays.
            <div className="apx-stagger-in" style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))', gap: 12}}>
              {list.map(a => {
                const meta = XPrim.categoryMeta(a.category);
                const tagStyle = meta.hue != null
                  ? { fontSize: 10, height: 18, padding:'0 6px', color: meta.fg, background: meta.bg, borderColor: meta.border }
                  : { fontSize: 10, height: 18, padding:'0 6px' };
                const cardStyle = {
                  padding: 18, cursor:'pointer',
                  ...(meta.hue != null ? { '--cat-dot': meta.dot } : null),
                };
                return (
                <div key={a.id} onClick={() => navigate('api-detail', {id: XApi.helpers.apiSlug(a)})}
                  className="card card-h"
                  style={cardStyle}>
                  <div className="row between">
                    <div className="row gap-2" style={{ alignItems:'flex-start' }}>
                      <ApiAvatar name={a.name} logoUrl={a.logoUrl} meta={meta} size={38} radius={8} fontSize={15}/>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, letterSpacing:'-0.01em' }}>{a.name} {a.verified && <span style={{color:'var(--fg-3)', fontWeight: 400}}>✓</span>}</div>
                        <div style={{
                          marginTop: 2,
                          fontFamily:'var(--font-mono)', fontSize: 10.5, fontWeight: 600,
                          letterSpacing:'0.08em', textTransform:'uppercase',
                          color: meta.hue != null ? meta.fg : 'var(--fg-3)',
                        }}>
                          {XPrim.categoryLabel(t, a.category)}
                        </div>
                      </div>
                    </div>
                    <button
                      className="fav-btn"
                      onClick={(e) => { e.stopPropagation(); authed ? toggleFavorite(a.id, favSet.has(a.id)) : navigate('login'); }}
                      title={favSet.has(a.id) ? t('market.favRemove') : t('market.favAdd')}
                      aria-pressed={favSet.has(a.id)}
                      style={{background:'none', border:'none', cursor:'pointer', padding: 2,
                        color: favSet.has(a.id) ? 'var(--c-amber)' : 'var(--fg-4)'}}>
                      <XIcons.IconStar size={13} fill={favSet.has(a.id) ? 'currentColor' : 'none'}/>
                    </button>
                  </div>
                  <p style={{fontSize: 12, color:'var(--fg-3)', lineHeight: 1.6, marginTop: 12, marginBottom: 14}} className="clamp-2">{a.description}</p>
                  <div className="row gap-2" style={{marginBottom: 12, flexWrap:'wrap', alignItems:'center'}}>
                    {a.tags.slice(0, 4).map(tag => <span key={tag} className="tag" style={tagStyle}>{tag}</span>)}
                  </div>
                  <div className="row between" style={{color:'var(--fg-4)', fontSize: 11}}>
                    <div className="row gap-2">
                      <span className="mono">{XPrim.fmt(a.hits)} {t('market.callsSuffix')}</span>
                      <span>·</span>
                      <span className="mono">{a.latency ? `${a.latency}ms` : '—'}</span>
                      <span>·</span>
                      <span className="mono" style={{color: a.uptime >= 99 ? 'var(--ok)' : 'var(--fg-4)'}}>{a.uptime}%</span>
                    </div>
                    {a.spark && <XPrim.Sparkline values={a.spark} width={56} height={18} />}
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="card apx-fade" style={{overflow:'hidden'}}>
              <table className="table">
                <thead>
                  <tr><th>{t('market.col.name')}</th><th>{t('market.col.category')}</th><th>{t('market.col.endpoints')}</th><th>{t('market.col.calls')}</th><th>{t('market.col.p95')}</th><th>{t('market.col.uptime')}</th><th></th></tr>
                </thead>
                <tbody>
                  {list.map(a => {
                    const meta = XPrim.categoryMeta(a.category);
                    return (
                    <tr key={a.id} style={{cursor:'pointer'}} onClick={() => navigate('api-detail', {id: XApi.helpers.apiSlug(a)})}>
                      <td>
                        <div className="row gap-2">
                          <ApiAvatar name={a.name} logoUrl={a.logoUrl} meta={meta} size={22} radius={4} fontSize={11}/>
                          <div>
                            <div>{a.name} {a.verified && <span style={{color:'var(--fg-3)'}}>✓</span>}</div>
                            <div className="micro-mono">{a.provider || a.host || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span style={{
                          display:'inline-flex', alignItems:'center', gap: 5,
                          padding:'2px 8px', borderRadius: 9,
                          background: meta.bg, border: `1px solid ${meta.border}`,
                          color: meta.fg, fontSize: 10,
                          fontFamily:'var(--font-mono)', letterSpacing:'0.04em',
                        }}>
                          <span style={{ width: 4, height: 4, borderRadius:'50%', background: meta.dot }}/>
                          {XPrim.categoryLabel(t, a.category)}
                        </span>
                      </td>
                      <td className="mono" style={{color:'var(--fg-2)'}}>{a.endpoints.length}</td>
                      <td className="mono">{XPrim.fmt(a.hits)}</td>
                      <td className="mono">{a.latency ? `${a.latency}ms` : '—'}</td>
                      <td><span className="mono" style={{color: a.uptime >= 99.95 ? 'var(--ok)' : 'var(--warn)'}}>{a.uptime}%</span></td>
                      <td><XIcons.IconArrowRight size={13}/></td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </main>
      </div>

      {/* Mobile filter bottom sheet — slides up from the bottom, covers the
          screen with a backdrop. Reuses the desktop FilterContent so changes
          stay in one place. */}
      {filtersOpen && (
        <>
          <div
            className="mobile-sheet-backdrop"
            onClick={() => setFiltersOpen(false)}
            aria-hidden="true"
          />
          <div
            className="market-filter-sheet"
            role="dialog"
            aria-label={t('market.filters')}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="row between" style={{ alignItems:'center', marginBottom: 14 }}>
              <div className="micro">{t('market.filters')}</div>
              <button
                onClick={() => setFiltersOpen(false)}
                className="btn btn-sm btn-ghost btn-icon"
                aria-label={t('common.close') || 'Close'}
              >
                <XIcons.IconX size={12}/>
              </button>
            </div>
            <FilterContent/>
            <div style={{ marginTop: 22, position:'sticky', bottom: 0, paddingTop: 14, background:'var(--bg-0)' }}>
              <button
                className="btn btn-primary"
                onClick={() => setFiltersOpen(false)}
                style={{ width:'100%', justifyContent:'center', height: 40 }}
              >
                {t('market.applyFilters')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const XMarketPages = { PortalPage, MarketPage };
export { PortalPage, MarketPage };
if (typeof window !== 'undefined') window.XMarketPages = XMarketPages;
